import React, { useState, useEffect } from 'react';
import api from '../api';

interface Attribute {
  id: number;
  name: string;
  type: string;
  options?: string[];
  required: boolean;
  category_id?: number;
}

interface AttributeManagerProps {
  productId?: number;
  categoryId?: number;
  attributes: Record<number, string>;
  onAttributesChange: (attributes: Record<number, string>) => void;
}

const AttributeManager: React.FC<AttributeManagerProps> = ({
  productId,
  categoryId,
  attributes,
  onAttributesChange
}) => {
  const [availableAttributes, setAvailableAttributes] = useState<Attribute[]>([]);

  const fetchAttributes = React.useCallback(async () => {
    try {
      const params = categoryId ? `?category_id=${categoryId}` : '';
      const response = await api.get(`/attributes/${params}`);
      setAvailableAttributes(response.data);
    } catch (error) {
      console.error('Error fetching attributes:', error);
    }
  }, [categoryId]);

  useEffect(() => {
    fetchAttributes();
  }, [fetchAttributes]);

  const handleAttributeChange = (attributeId: number, value: string) => {
    const newAttributes = { ...attributes, [attributeId]: value };
    onAttributesChange(newAttributes);
  };

  const renderAttributeInput = (attribute: Attribute) => {
    const value = attributes[attribute.id] || '';

    switch (attribute.type) {
      case 'select':
        return (
          <select
            className="form-select"
            value={value}
            onChange={(e) => handleAttributeChange(attribute.id, e.target.value)}
            required={attribute.required}
          >
            <option value="">Select...</option>
            {attribute.options?.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      
      case 'number':
        return (
          <input
            type="number"
            className="form-input"
            value={value}
            onChange={(e) => handleAttributeChange(attribute.id, e.target.value)}
            required={attribute.required}
          />
        );
      
      case 'boolean':
        return (
          <select
            className="form-select"
            value={value}
            onChange={(e) => handleAttributeChange(attribute.id, e.target.value)}
            required={attribute.required}
          >
            <option value="">Select...</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );
      
      default:
        return (
          <input
            type="text"
            className="form-input"
            value={value}
            onChange={(e) => handleAttributeChange(attribute.id, e.target.value)}
            required={attribute.required}
          />
        );
    }
  };

  if (availableAttributes.length === 0) {
    return null;
  }

  return (
    <div>
      <h4>Product Attributes</h4>
      {availableAttributes.map(attribute => (
        <div key={attribute.id} className="form-group">
          <label className="form-label">
            {attribute.name}
            {attribute.required && <span style={{ color: 'red' }}>*</span>}
          </label>
          {renderAttributeInput(attribute)}
        </div>
      ))}
    </div>
  );
};

export default AttributeManager;